import React, { useState, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { FiSun, FiMoon, FiGrid, FiSettings, FiMessageSquare, FiLogOut } from "react-icons/fi";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { navConfig, parallaxNavConfig } from "../../config/navConfig";
import { appVariant, isSection } from "../../config/appVariant";

// Detect touch device
const isTouchDevice = () => {
	return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const Sidebar = ({ isLazloOpen, onLazloToggle }) => {
	const location = useLocation();
	const isTouch = useRef(isTouchDevice());
	const { mode, toggleMode } = useTheme();
	const { logout } = useAuth();

	// Track which flyout is open (for touch devices)
	const [activeFlyout, setActiveFlyout] = useState(null);

	// Check if a link is active
	const isLinkActive = (to) => {
		if (location.pathname === to) return true;
		const basePath = to.split("/")[1] ? `/${to.split("/")[1]}` : to;
		return location.pathname.startsWith(basePath + "/");
	};

	// Check if section has any active link (including homePath for direct-nav sections)
	const isSectionActive = (section) => {
		if (section.links.length === 0) return isLinkActive(section.homePath);
		return section.links.some((link) => isLinkActive(link.to));
	};

	// Pick nav config based on variant
	const sections = appVariant.nav === 'parallax'
		? parallaxNavConfig
		: navConfig.filter(s => isSection(s.id));

	// Handle section click (touch devices toggle flyout)
	const handleSectionClick = (sectionId, e) => {
		if (isTouch.current) {
			e.preventDefault();
			setActiveFlyout(activeFlyout === sectionId ? null : sectionId);
		}
	};

	// Close flyout when clicking a link
	const handleLinkClick = () => {
		if (isTouch.current) {
			setActiveFlyout(null);
		}
	};

	return (
		<aside className="sidebar">
			{/* Logo */}
			<div className="sidebar__header">
				<Link to={appVariant.defaultRoute} className="sidebar__logo" title="Dashboard">
					<span className="sidebar__logo-icon">
						<FiGrid />
					</span>
				</Link>
			</div>

			{/* Navigation */}
			<nav className="sidebar__nav">
				{sections.map((section) => {
					const Icon = section.icon;
					const isActive = isSectionActive(section);
					const isFlyoutOpen = activeFlyout === section.id;
					const hasFlyout = section.links.length > 0;

					return (
						<div
							className={`sidebar__section ${isActive ? "sidebar__section--active" : ""} ${isFlyoutOpen ? "sidebar__section--flyout-open" : ""}`}
							key={section.id}
						>
							<Link
								to={section.homePath}
								className="sidebar__section-btn"
								title={section.title}
								onClick={(e) => hasFlyout && handleSectionClick(section.id, e)}
							>
								<Icon className="sidebar__section-icon" />
							</Link>

							{/* Flyout Panel - only render when section has sub-links */}
							{hasFlyout && (
								<div className={`sidebar__flyout ${isFlyoutOpen ? "sidebar__flyout--open" : ""}`}>
									<div className="sidebar__flyout-header">
										<span className="sidebar__flyout-title">{section.title}</span>
									</div>
									<div className="sidebar__flyout-links">
										{section.links.map((link) => (
											<NavLink
												key={link.to}
												to={link.to}
												className={({ isActive }) =>
													`sidebar__flyout-link ${isActive || isLinkActive(link.to) ? "sidebar__flyout-link--active" : ""}`
												}
												onClick={handleLinkClick}
											>
												{link.label}
											</NavLink>
										))}
									</div>
								</div>
							)}
						</div>
					);
				})}
			</nav>

			{/* Footer */}
			<div className="sidebar__divider" />
			<div className="sidebar__footer">
				{/* Light/Dark Toggle */}
				<button
					className="sidebar__footer-btn"
					onClick={toggleMode}
					title={mode === 'dark' ? 'Light mode' : 'Dark mode'}
				>
					{mode === 'dark' ? <FiSun className="sidebar__footer-icon" /> : <FiMoon className="sidebar__footer-icon" />}
				</button>

				{/* Lazlo Chat Toggle */}
				{appVariant.showLazlo && (
					<button
						className={`sidebar__footer-btn ${isLazloOpen ? 'sidebar__footer-btn--active' : ''}`}
						onClick={onLazloToggle}
						title={isLazloOpen ? 'Close Lazlo' : 'Open Lazlo'}
					>
						<FiMessageSquare className="sidebar__footer-icon" />
					</button>
				)}

				{/* Settings - hidden in parallax where it's in the main nav */}
				{appVariant.nav !== 'parallax' && (
					<NavLink
						to="/settings"
						className="sidebar__footer-btn"
						title="Settings"
					>
						<FiSettings className="sidebar__footer-icon" />
					</NavLink>
				)}

				{/* Logout */}
				<button
					className="sidebar__footer-btn"
					onClick={logout}
					title="Log out"
				>
					<FiLogOut className="sidebar__footer-icon" />
				</button>
			</div>
		</aside>
	);
};

export default Sidebar;
